import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import "./reportsPage.css";
import { CombinedContext } from "../../context/combinedContext";
import translations from "../../translations/translations";
import reportsRequest from "../../request/reportsRequest";
import SalesPanel from "./salesPanel/salesPanel";
import CustomersPanel from "./customersPanel/customersPanel";
import CustomerDetailModal from "./customerDetailModal/customerDetailModal";
import { BarChart3, CalendarDays, CalendarRange, Layers, Users } from "lucide-react";
import CustomSelect from "../common/customSelect/customSelect";
import {
	rangeForPreset,
	allowedGranularities,
	defaultGranularity,
	formatRangeLabel,
	rangeLengthDays,
	fill,
} from "./reportsFormat";

const PRESETS = ["last7", "last30", "last90", "thisMonth", "lastMonth", "thisYear", "custom"];
const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 350;

const ReportsPage = () => {
	const { userData, language } = useContext(CombinedContext);
	const t = translations[language].reportsPage;

	const role = userData?.user?.role;
	const shopId = userData?.user?.shopId;
	const accessToken = userData?.cognitoTokens?.AccessToken;
	const isStaff = role === "staff";

	const [tab, setTab] = useState("sales");

	const [preset, setPreset] = useState("last30");
	const [range, setRange] = useState(() => rangeForPreset("last30"));
	// Held apart from `range` so a half-typed custom range never triggers a fetch.
	const [draftRange, setDraftRange] = useState(() => rangeForPreset("last30"));
	const [granularity, setGranularity] = useState(() => {
		const r = rangeForPreset("last30");
		return defaultGranularity(r.from, r.to);
	});

	const [sales, setSales] = useState(null);
	const [salesLoading, setSalesLoading] = useState(true);
	const [salesError, setSalesError] = useState(null);

	const [customers, setCustomers] = useState(null);
	const [customersLoading, setCustomersLoading] = useState(false);
	const [customersError, setCustomersError] = useState(null);
	const [search, setSearch] = useState("");
	const [debouncedSearch, setDebouncedSearch] = useState("");
	const [sort, setSort] = useState("lastVisit");
	const [offset, setOffset] = useState(0);

	const [openKey, setOpenKey] = useState(null);
	const [detail, setDetail] = useState(null);
	const [detailLoading, setDetailLoading] = useState(false);
	const [detailError, setDetailError] = useState(null);

	/* Every fetch below can be outrun by a newer one — changing the range while a
	   slow request is in flight would otherwise let the stale response overwrite
	   the fresh one. Each effect stamps a request id and drops its own result if
	   a later request has already started. */
	const salesReq = useRef(0);
	const customersReq = useRef(0);
	const detailReq = useRef(0);

	const granularityOptions = useMemo(
		() => allowedGranularities(range.from, range.to),
		[range.from, range.to],
	);

	/* Switching to a shorter or longer period can strand the current grouping —
	   "Month" over seven days, say. Pull it back to something readable rather
	   than rendering a chart with one bar in it. */
	useEffect(() => {
		if (!granularityOptions.includes(granularity)) {
			setGranularity(defaultGranularity(range.from, range.to));
		}
	}, [granularityOptions, granularity, range.from, range.to]);

	useEffect(() => {
		const id = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
		return () => clearTimeout(id);
	}, [search]);

	// A new search or sort invalidates the current page position.
	useEffect(() => {
		setOffset(0);
	}, [debouncedSearch, sort]);

	const loadSales = useCallback(async () => {
		if (!shopId || !accessToken) return;
		const id = ++salesReq.current;

		setSalesLoading(true);
		setSalesError(null);

		try {
			const res = await reportsRequest.getSalesReport(
				shopId,
				{ from: range.from, to: range.to, granularity },
				accessToken,
			);
			if (id !== salesReq.current) return;
			setSales(res);
		} catch (err) {
			if (id !== salesReq.current) return;
			console.error("Failed to load sales report:", err);
			setSalesError(t.errors.load);
		} finally {
			if (id === salesReq.current) setSalesLoading(false);
		}
	}, [shopId, accessToken, range, granularity, t]);

	/* No from/to. The client book is lifetime by definition — a visit count, a
	   first visit and a total spent are not statements about a date range — and
	   scoping it to the period picker above meant a five-year regular showed up
	   as a first-timer whenever someone chose "last 7 days". The API reads an
	   absent range here as all-time. */
	const loadCustomers = useCallback(async () => {
		if (!shopId || !accessToken) return;
		const id = ++customersReq.current;

		setCustomersLoading(true);
		setCustomersError(null);

		try {
			const res = await reportsRequest.getCustomers(
				shopId,
				{ search: debouncedSearch, sort, limit: PAGE_SIZE, offset },
				accessToken,
			);
			if (id !== customersReq.current) return;
			setCustomers(res);
		} catch (err) {
			if (id !== customersReq.current) return;
			console.error("Failed to load customers:", err);
			setCustomersError(t.errors.loadCustomers);
		} finally {
			if (id === customersReq.current) setCustomersLoading(false);
		}
	}, [shopId, accessToken, debouncedSearch, sort, offset, t]);

	useEffect(() => {
		if (tab === "sales") loadSales();
	}, [tab, loadSales]);

	useEffect(() => {
		if (tab === "customers") loadCustomers();
	}, [tab, loadCustomers]);

	// All-time as well, so the history in the modal matches the totals beside it.
	useEffect(() => {
		if (!openKey || !shopId || !accessToken) return;
		const id = ++detailReq.current;

		setDetailLoading(true);
		setDetailError(null);
		setDetail(null);

		(async () => {
			try {
				const res = await reportsRequest.getCustomerDetail(shopId, openKey, {}, accessToken);
				if (id !== detailReq.current) return;
				setDetail(res);
			} catch (err) {
				if (id !== detailReq.current) return;
				console.error("Failed to load customer detail:", err);
				setDetailError(t.errors.loadDetail);
			} finally {
				if (id === detailReq.current) setDetailLoading(false);
			}
		})();
	}, [openKey, shopId, accessToken, t]);

	const onPresetChange = (next) => {
		setPreset(next);
		if (next === "custom") {
			setDraftRange(range);
			return;
		}
		const computed = rangeForPreset(next);
		setDraftRange(computed);
		setRange(computed);
	};

	const applyCustomRange = () => {
		if (draftRange.from && draftRange.to && draftRange.from > draftRange.to) return;
		setRange(draftRange);
	};

	/* Fills the right-hand half of the filter bar, which was empty. A preset
	   names a rule rather than a period, so this is the only place the report
	   says which dates it actually settled on. */
	const rangeSummary = useMemo(
		() => ({
			label: formatRangeLabel(range.from, range.to, language),
			days: rangeLengthDays(range.from, range.to),
		}),
		[range.from, range.to, language],
	);

	const customRangeInvalid = useMemo(
		() => preset === "custom" && draftRange.from && draftRange.to && draftRange.from > draftRange.to,
		[preset, draftRange],
	);

	if (!shopId) {
		return (
			<div className="sf-rap-page">
				<div className="sf-rap-content">
					<p className="sf-rap-error">{t.errors.noShop}</p>
				</div>
			</div>
		);
	}

	const TAB_ICONS = { sales: BarChart3, customers: Users };

	return (
		<div className="sf-rap-page">
			<div className="sf-rap-content">
				<header className="sf-rap-head">
					<div className="sf-rap-head-row">
						<div className="sf-rap-head-copy">
							<h1 className="sf-rap-title">{t.header.title}</h1>
							<p className="sf-rap-subtitle">{isStaff ? t.header.subtitleStaff : t.header.subtitle}</p>
						</div>

						<nav className="sf-rap-tabs" role="tablist">
							{["sales", "customers"].map((key) => {
								const Icon = TAB_ICONS[key];
								return (
									<button
										key={key}
										type="button"
										role="tab"
										aria-selected={tab === key}
										className={`sf-rap-tab ${tab === key ? "is-active" : ""}`}
										onClick={() => setTab(key)}
									>
										<Icon size={16} />
										{t.tabs[key]}
									</button>
								);
							})}
						</nav>
					</div>
				</header>

				{/* Sales only. The client list carries no period control — see
				    loadCustomers — so rendering the picker there offered a choice
				    that changed nothing on screen. */}
				{tab === "sales" && (
					<section className="sf-rap-controls">
						<div className="sf-rap-controls-group">
							{/* div, not label: CustomSelect is not a form control. */}
							<div className="sf-rap-field">
								<span>
									<CalendarRange size={12} />
									{t.range.label}
								</span>
								<CustomSelect
									className="sf-rap-select"
									value={preset}
									onChange={onPresetChange}
									options={PRESETS.map((p) => ({ value: p, label: t.range[p] }))}
								/>
							</div>

							{preset === "custom" && (
								<>
									<label className="sf-rap-field">
										<span>
											<CalendarDays size={12} />
											{t.range.from}
										</span>
										<input
											type="date"
											value={draftRange.from}
											max={draftRange.to || undefined}
											onChange={(e) => setDraftRange((r) => ({ ...r, from: e.target.value }))}
										/>
									</label>
									<label className="sf-rap-field">
										<span>
											<CalendarDays size={12} />
											{t.range.to}
										</span>
										<input
											type="date"
											value={draftRange.to}
											min={draftRange.from || undefined}
											onChange={(e) => setDraftRange((r) => ({ ...r, to: e.target.value }))}
										/>
									</label>
									<button
										type="button"
										className="sf-rap-btn sf-rap-btn--primary"
										onClick={applyCustomRange}
										disabled={Boolean(customRangeInvalid)}
									>
										{t.range.apply}
									</button>
								</>
							)}

							{granularityOptions.length > 1 && (
								<div className="sf-rap-field">
									<span>
										<Layers size={12} />
										{t.granularity.label}
									</span>
									<CustomSelect
										className="sf-rap-select"
										value={granularity}
										onChange={setGranularity}
										options={granularityOptions.map((g) => ({ value: g, label: t.granularity[g] }))}
									/>
								</div>
							)}
						</div>

						{/* The dates the preset resolved to. Fills the right of the bar,
						    which was empty, with the one thing "Last 30 days" does not
						    tell you. Hidden below 720px, where the controls need the width
						    more than the caption does. */}
						<div className="sf-rap-controls-summary">
							<CalendarDays size={16} />
							<span className="sf-rap-controls-summary-text">
								<strong>{rangeSummary.label}</strong>
								<span>{fill(t.range.covering, { days: rangeSummary.days })}</span>
							</span>
						</div>
					</section>
				)}

				{tab === "sales" && customRangeInvalid && <p className="sf-rap-error">{t.errors.badRange}</p>}

				{tab === "sales" && (
					<>
						{salesLoading && (
							<div className="sf-rap-loading">
								<span className="sf-rap-spinner" />
							</div>
						)}
						{!salesLoading && salesError && (
							<div className="sf-rap-error-box">
								<p className="sf-rap-error">{salesError}</p>
								<button type="button" className="sf-rap-btn" onClick={loadSales}>
									{t.retry}
								</button>
							</div>
						)}
						{!salesLoading && !salesError && sales && (
							<SalesPanel report={sales} granularity={granularity} language={language} t={t} />
						)}
					</>
				)}

				{tab === "customers" && (
					<>
						{customersError && (
							<div className="sf-rap-error-box">
								<p className="sf-rap-error">{customersError}</p>
								<button type="button" className="sf-rap-btn" onClick={loadCustomers}>
									{t.retry}
								</button>
							</div>
						)}
						{!customersError && customersLoading && !customers && (
							<div className="sf-rap-loading">
								<span className="sf-rap-spinner" />
							</div>
						)}
						{!customersError && customers && (
							<div className={customersLoading ? "sf-rap-stale" : ""}>
								<CustomersPanel
									data={customers}
									search={search}
									onSearchChange={setSearch}
									sort={sort}
									onSortChange={setSort}
									onOpen={setOpenKey}
									onPage={setOffset}
									t={t}
									language={language}
								/>
							</div>
						)}
					</>
				)}
			</div>

			{openKey && (
				<CustomerDetailModal
					detail={detail}
					loading={detailLoading}
					error={detailError}
					onClose={() => setOpenKey(null)}
					t={t}
					language={language}
				/>
			)}
		</div>
	);
};

export default ReportsPage;
